import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, OnInit, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { BreakpointService } from '@ecm/consumer';
import { EcmMaterialModule, HtmlSanitizerPipe, ModalWrapperService } from '@ecm/material';
import { PortfolioFlagsService } from '@ecm/portfolio';
import { EcmRouterModule, Link, LinkService, TrackingData } from '@ecm/router';

import { Legal } from '../../../business/api/dashboardV7/legal';
import { Tag } from '../../../business/api/dashboardV7/tag';
import { TmsCodes } from '../../../business/model/tracking/tmsCodes.enum';
import { UrlService } from '../../../services/url.service';
import { ParamsUtils } from '../../../utils/params.utils';
import { MegapushLegalModalComponent } from './legal-modal/megapush-legal-modal.component';
import { MegapushUtils } from './megapush.utils';

@Component({
    selector: 'megapush',
    templateUrl: 'megapush.html',
    styleUrls: ['megapush.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [NgTemplateOutlet, EcmMaterialModule, EcmRouterModule, HtmlSanitizerPipe, NgClass],
})
export class MegapushComponent implements OnInit {
    private readonly _breakpointService: BreakpointService = inject(BreakpointService);
    private readonly _modalWrapperService = inject(ModalWrapperService);
    private readonly _portfolioFlagsService: PortfolioFlagsService = inject(PortfolioFlagsService);
    private readonly _urlService: UrlService = inject(UrlService);
    public readonly _linkService: LinkService = inject(LinkService);

    // max-width < 735px
    public isMobileWidth = toSignal(this._breakpointService.isMobileWidth());
    public isSosh = computed(() => this._portfolioFlagsService.isSosh());

    public cid = input<string>();
    public textColor = input.required({
        transform: (value: string) => (value === 'black' ? 'text-dark' : 'text-white'),
    });
    public energy = input<string, string>(undefined, {
        transform: (value: string) => (value ? `assets/dashboard/images/energy-${value.toUpperCase()}.svg` : undefined),
    });
    public img161 = input.required<string>();
    public img162 = input.required<string>();
    public legal = input<Legal>();
    public tag = input<Tag>();
    public text = input.required<string>();
    public text2 = input<string>();
    public title = input.required<string>();
    public trackingData = input<TrackingData>();
    public urlLabel = input.required<string>();
    public urlWeb = input.required<Link>();
    public hrefWeb = computed(() => this._linkService.getLinkSync(this.urlWeb(), ParamsUtils.cidMap(this.cid())));
    public tagText = computed(() => {
        const text = this.tag()?.text;
        if (!text) return '';
        const hasTag = text.includes('[tagyellow]') || text.includes('[tagblack]') || text.includes('[tagwhite]');
        const wrappedText = hasTag ? text : '[tagyellow]' + text + '[/tagyellow]';
        return MegapushUtils.transformContent(wrappedText);
    });
    public tagDate = computed(() => (this.tag()?.date ? `&nbsp;${this.tag().date}` : undefined));
    public textContent = computed(() =>
        MegapushUtils.transformContent(this.text(), this.textColor(), this.isMobileWidth()),
    );
    public titleContent = computed(() =>
        MegapushUtils.transformContent(this.title(), this.textColor(), this.isMobileWidth()),
    );
    public trackingDatas = computed(() => {
        if (!this.trackingData()) {
            return undefined;
        }
        if (this.cid()) {
            this.trackingData().cappingTracking.cid = this.cid();
        }
        return this.trackingData();
    });

    public zid?: string;
    public isDisabledEclairLink?: boolean;

    protected readonly ParamsUtils = ParamsUtils;

    public ngOnInit(): void {
        if (this.isSosh()) {
            this.zid = this.isMobileWidth()
                ? TmsCodes.care_digital_megapush_sosh_zidMob
                : TmsCodes.care_digital_megapush_sosh_zid;
        } else {
            this.zid = this.isMobileWidth()
                ? TmsCodes.care_digital_megapush_zidMob
                : TmsCodes.care_digital_megapush_zid;
        }
        if (this.urlWeb()) {
            this.isDisabledEclairLink = this._urlService.isDisabledEclairLink(this.urlWeb());
        }
    }

    public openLink(): void {
        this._linkService.openLink(this.urlWeb(), this.trackingDatas(), ParamsUtils.cidMap(this.cid()));
    }

    public openLegalModal(): void {
        const modalRef = this._modalWrapperService.open(MegapushLegalModalComponent, this.isSosh(), {
            windowClass: 'megapush-legal-modal',
            backgroundColor: this.legal()?.background?.trim(),
        });
        modalRef.componentInstance.legal = this.legal();
    }
}
